import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Avatar, Flex, Select, Tooltip } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Container, Modal, Col, Row } from 'react-bootstrap'
import { useToasts } from '../../contexts/toasts'
import { useFullScreenLoader } from '../../contexts/fullScreenLoader'
import { EnvVariable } from '../../models/Environment'
import { EnvironmentVariables } from '../../api/EnvironmentVariables'
import AlidaLoader from '../../components/common/AlidaLoader'
import AppBrowsingToolbar, { Filters } from '../../components/common/AppBrowsingToolbar'
import { accessLevelOptions, formatDate } from '../../utils'
import { DefaultOptionType } from 'antd/es/select'
import { AccessLevel, PagedResults } from '../../models/generics'
import EnvironmentVariableForm from '../../components/forms/EnvironmentVariableForm'
import { EventEmitter } from '../../utils/EventEmitter'
import { useAuth } from '../../contexts/auth'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { sectionLabelsSelector } from '../../slices/sectionsSlice'
import AppTag from '../../components/common/AppTag'
import AppBadge from '../../components/common/AppBadge'

const environmentVariablesFilters: Filters = {
  'search': { placeholder: 'Search variable...', query: 'freeText' },
  'accessLevel': {
    placeholder: 'Select privacy level',
    select: true,
    query: 'accessLevel',
    type: 'string',
    options: accessLevelOptions
      .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true),
    customOptions: true,
    renderOption: (option: DefaultOptionType, index: number) => (
      <Select.Option key={index} value={option.value} label={option.label}>
        <div>
          <FontAwesomeIcon icon={option.icon} fixedWidth />
          <span className="ms-2">{option.label}</span>
        </div>
      </Select.Option>
    )
  },
  'tags': {
    placeholder: 'Select  tags...',
    select: true,
    query: 'tags',
    type: 'string',
    options: [],
    selectProps: {
      mode: "tags",
    },
    col: 3,
  },
}

export default function EnvironmentVariablesList() {
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const { loggedUser } = useAuth();
  const [latestParams, setLatestParams] = useState<URLSearchParams>();

  const [environmentVariables, setEnvironmentVariables] = useState<EnvVariable[]>();
  const [totalEnvs, setTotalEnvs] = useState<number>();
  const [error, setError] = useState(false);
  const [variable, setVariable] = useState<EnvVariable>();
  const [editing, setEditing] = useState<number>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const variableLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "environment-variables"));

  const initEnvironmentVariables = useCallback((queryParams?: Record<string, any>) => {
    const params = new URLSearchParams(queryParams || latestParams);
    if (queryParams) setLatestParams(params);

    EnvironmentVariables.getEnvironmentVariables(params).then(
      ({ data: envPages }) => {
        setEnvironmentVariables((envPages as PagedResults<EnvVariable>).content);
        setTotalEnvs((envPages as PagedResults<EnvVariable>).pageMetadata.totalElements);
      },
      (err) => {
        showToast('environments-error', `Could not get ${variableLabels.plural}`, `${variableLabels.plural} Error`, 'danger');
        setError(true);
        setEnvironmentVariables([]);
      }
    )
  }, [showToast, latestParams, variableLabels]);

  const reset = useCallback((params?: Record<string, any>) => {
    setError(false);
    setEnvironmentVariables(undefined);
    initEnvironmentVariables(params);
  }, [initEnvironmentVariables]);

  useEffect(() => {
    EventEmitter.subscribe('browsing:paramsChanged', reset);
    return () => EventEmitter.unsubscribe('browsing:paramsChanged');
  }, [reset]);

  const handleClose = useCallback(() => {
    setShowDeleteModal(false);
    setShowEditModal(false);
    setVariable(undefined);
    setEditing(undefined);
  }, []);

  const handleDelete = useCallback((env: EnvVariable) => () => {
    setShowDeleteModal(true);
    setVariable(env);
  }, []);

  const openCreateModal = useCallback(() => setShowEditModal(true), []);

  const handleEdit = useCallback((env: EnvVariable, index: number) => () => {
    setEditing(index);
    setVariable(env);
    setShowEditModal(true);
  }, []);

  const handleSubmit = useCallback((values: EnvVariable) => {
    showLoader();
    const promise = editing !== undefined
      ? EnvironmentVariables.updateEnvironmentVariable(variable!.id!, values)
      : EnvironmentVariables.createEnvironmentVariable(values);

    promise.then(
      () => {
        hideLoader();
        showToast('environment-variable-success', `${variableLabels.singular} ${editing !== undefined ? 'updated' : 'created'} successfully`, `${variableLabels.singular} ${editing !== undefined ? 'Updated' : 'Created'}`, 'success');
        handleClose();
        reset();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('environment-variable-error', message, `${variableLabels.singular} ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [handleClose, reset, showLoader, hideLoader, editing, showToast, variable, variableLabels]);

  const renderAsyncContent = () => {
    return environmentVariables
      ? renderEnvironmentVariables()
      : (
        <div className="d-flex justify-content-center py-5">
          <AlidaLoader size={300} />
        </div>
      );
  };

  const deleteEnvironmentVariable = useCallback(() => {
    showLoader();

    EnvironmentVariables.deleteEnvironmentVariable(variable!.id!)
      .then(
        () => {
          hideLoader();
          handleClose();
          reset();
          showToast('environment-variable-success', `${variableLabels.singular} deleted successfully`, `${variableLabels.singular} Success`, 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('environment-variable-error', message, `${variableLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showLoader, hideLoader, handleClose, variable, reset, showToast, variableLabels]);

  const renderEnvironmentVariables = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    return environmentVariables!.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {variableLabels.plural} found.</span>
        </Alert>
      ) : (
        <Row className="gx-2">
          {environmentVariables!.map((env, index) => {
            const canEdit = env.accessLevel === AccessLevel.PUBLIC || (env.accessLevel === AccessLevel.TEAM && loggedUser?.userOrganizationStatus?.current?.id === env.organization?.id) || env.owner?.id === loggedUser?.sub;
            return (
              <Col key={env.id} md={4}>
                <Card className="shadow-sm mb-3">
                  <Flex className="p-1">
                    <Badge className="shadow">{env.accessLevel}</Badge>
                    {env.isSecret && (
                      <AppBadge className="ms-2" color="#eda60e" solid>
                        <FontAwesomeIcon icon={solid('lock')} />
                        <span className="ms-1">Secret</span>
                      </AppBadge>
                    )}
                    <small className="ms-auto font-sm text-muted text-end">
                      <b>Created:</b>
                      <span className="ms-1">{formatDate(env.createDateTime!)}</span>
                    </small>
                  </Flex>
                  <Card.Body className="p-2">
                    <Flex align="center">
                      <Avatar
                        src={env.img}
                        icon={<FontAwesomeIcon icon={solid('image')} size="lg" />}
                        className="border me-1"
                        style={{ height: 50, width: 50 }}
                      />
                      <Col className="ms-2 overflow-hidden">
                        <Link to={`/environment-variables/${env.id}`} className="fw-bold mb-1 text-truncate">{env.name}</Link>
                        <Tooltip title={env.description}>
                          <p className="text-secondary text-truncate mb-0">{env.description}</p>
                        </Tooltip>
                      </Col>
                      {(env.owner?.id === loggedUser?.sub || canEdit) && (
                        <Flex className="gap-2" align="center" justify="flex-end">
                          {!env.isSecret && (
                            <button className="circle-button dark ms-2" onClick={handleEdit(env, index)}>
                              <FontAwesomeIcon icon={solid('pen')} />
                            </button>
                          )}
                          <button className="circle-button dark" onClick={handleDelete(env)}>
                            <FontAwesomeIcon icon={solid('trash')} />
                          </button>
                        </Flex>
                      )}
                    </Flex>
                  </Card.Body>

                  <Flex className="tags-wrapper m-1">
                    {(env.tags || []).length === 0 && <AppTag noTags />}
                    {env.tags!.map(tag => <AppTag key={tag} tag={tag} className="me-1" />)}
                  </Flex>
                </Card>
              </Col>
            );
          })}
        </Row>
      );
  }, [loggedUser, environmentVariables, error, handleDelete, handleEdit, variableLabels]);

  return (
    <Container fluid="md" className="mt-3">
      <AppBrowsingToolbar
        title={`User ${variableLabels.plural}`}
        actions={
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Create {variableLabels.singular}</span>
          </Button>
        }
        total={totalEnvs}
        filters={environmentVariablesFilters}
      />
      <hr />

      {renderAsyncContent()}

      <Modal show={showEditModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editing !== undefined ? `Edit ${variableLabels.singular}` : `New ${variableLabels.singular}`}</Modal.Title>
        </Modal.Header>
        <EnvironmentVariableForm environmentVariable={variable} onSubmit={handleSubmit} onCancel={handleClose} />
      </Modal>

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {variableLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {variableLabels.singular} <b>{variable?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteEnvironmentVariable}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
